import { makeAutoObservable } from "mobx";
import FirebaseService from "./FirebaseService";
import { addDoc, collection, doc, DocumentData, getDoc, getDocs, limit, or, orderBy, query, QueryDocumentSnapshot, QueryFilterConstraint, setDoc, startAfter, where } from "firebase/firestore";
import { getBlob, ref, uploadBytes } from "firebase/storage";
import { ControlPoint } from "./PlansService";
import triGram from "../utils/triGram";

export type Race = {
    id: string,
    name: string;
    main: string;
    createdAt: Date;
    createdBy: string;
    website: string;
    distance: number;
    elevationGain: number;
    elevationLoss: number;
    city: string;
    country: string;
    svg?: string;
    sport?: "run" | "bike";
    controlPoints?: ControlPoint[];
};

export type RaceGpxSlope = {
    startDistance: number;
    endDistance: number;
    type: "up" | "down" | "flat"
};

export type RaceRequest = {
    raceName: string;
    raceWebsite: string;
    raceDistance: string;
    userId: string;
    userEmail: string;
    createdAt: Date;
};

export default class RacesService {


    get races() {
        return this._races;
    }

    get isLoadingRaces() {
        return this._isLoadingRaces;
    }

    get hasMoreRaces() {
        return this._hasMoreRaces;
    }

    private _firebaseService: FirebaseService;
    private _currentLatestRace: QueryDocumentSnapshot<DocumentData, DocumentData> | null = null;
    private _hasMoreRaces = true;

    private _races: Race[] = [];
    private _isLoadingRaces = false;

    constructor(firebaseService: FirebaseService) {

        this._firebaseService = firebaseService;
        makeAutoObservable(this, undefined, { autoBind: true, deep: false })
    }

    async loadRace(raceId: string): Promise<Race | null> {

        const db = this._firebaseService.db;
        const docRef = doc(db, `races/${raceId}`);
        return getDoc(docRef).then((docSnapshot) => {
            if (!docSnapshot.exists()) {
                return null;
            }
            const docSnapshotData = docSnapshot.data();
            return docSnapshotData as Race
        }).catch(() => {
            return null;
        });
    }

    async loadRaces(fromStart: boolean = false) {
        if (this._isLoadingRaces) {
            return;
        }

        if (fromStart) {
            this._currentLatestRace = null;
            this._hasMoreRaces = true;
            this._races = [];
        }

        this._isLoadingRaces = true;
        const db = this._firebaseService.db;

        const collectionRef = collection(db, "races");
        const q = this._currentLatestRace ? query(collectionRef, orderBy("name", "asc"), limit(10), startAfter(this._currentLatestRace)) :
            query(collectionRef, orderBy("name", "asc"), limit(10));


        const querySnapshot = await getDocs(q);
        if (querySnapshot.docs.length < 10) {
            this._hasMoreRaces = false;
        }
        if (querySnapshot.docs.length > 0) {
            this._currentLatestRace = querySnapshot.docs[querySnapshot.docs.length - 1];
        }

        this._races = [...this.races, ...querySnapshot.docs.map(
            (docSnapshot) => {
                const raceData = docSnapshot.data();
                return raceData as Race
            })];
        this._isLoadingRaces = false;
    }

    async searchRacesByName(name: string): Promise<Race[]> {
        
        if (!name || name.trim().length < 3) return [];

        const db = this._firebaseService.db;
        const collectionRef = collection(db, "races");

        let trigrams = Object.keys(triGram(name));

        if (trigrams.length > 30) {
            trigrams = trigrams.slice(0, 30);
        }

        const searchConstraints: QueryFilterConstraint[] = [];
        trigrams.forEach(triGram =>
            searchConstraints.push(where(`triGram.${triGram}`, '==', true))
        );

        const q = query(collectionRef, or(...searchConstraints));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map((docSnapshot) => {
            const raceData = docSnapshot.data();
            return raceData as Race;
        });
    }

    async addRaceRequest(raceRequest: RaceRequest) {
        const db = this._firebaseService.db;
        const collectionRef = collection(db, "raceRequests");

        return addDoc(collectionRef, raceRequest);
    };

    async upsertRace(race: Race) {
        const db = this._firebaseService.db;
        const documentnRef = doc(db, `races/${race.id}`);

        return setDoc(documentnRef, race);
    }

    async addGpxFile(raceId: string, file: File) {

        const storage = this._firebaseService.storage;
        const storageRef = ref(storage, `races/${raceId}.gpx`);

        return uploadBytes(storageRef, file);
    }

    async getGpxFile(raceId: string): Promise<File | null> {
        const storage = this._firebaseService.storage;
        const storageRef = ref(storage, `races/${raceId}.gpx`);
        return getBlob(storageRef).then((gpxFile) =>
            new File([gpxFile], raceId),
        ).catch(() => {
            return null;
        });
    }
}